import axios from "axios";
import { v4 as uuid } from "uuid";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  ClientMetadata,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { storage } from "@/common/services/storage";
import {
  AuthenticationRequest,
  AuthenticationResponse,
  ConfirmResetPasswordRequest,
  ConfirmSignupRequest,
  ResetPasswordRequest,
  SignupRequest,
} from "@/common/models/authentication";
import { API_ENDPOINT, COGNITO_DOMAIN, COGNITO_REDIRECT_URI } from "@/common/constants";
import { UserResponse } from "@/common/models/user";

interface CognitoError {
  code: string;
  name: string;
  message: string;
  stack: string;
}

const COGNITO_USER_POOL_ID = process.env.VUE_APP_COGNITO_USER_POOL_ID;
const COGNITO_CLIENT_ID = process.env.VUE_APP_COGNITO_CLIENT_ID;
const TOKEN_STORAGE_KEY = "token";

const isCognitoError = (err: Error, code: string): boolean => (err as CognitoError).code === code;

const userPool = (Pool: CognitoUserPool, Username: string) => new CognitoUser({ Username, Pool });

const signupClientMetadata = (
  alias: string,
  name: string | undefined,
  surname: string | undefined,
): ClientMetadata => {
  const metadata: ClientMetadata = { alias };
  if (name) metadata.name = name;
  if (surname) metadata.surname = surname;
  return metadata;
};

const loginStandard = async (creds: AuthenticationRequest): Promise<void> => {
  const { data } = await axios.post<AuthenticationResponse>(`${API_ENDPOINT}/login`, creds);
  await storage.set(TOKEN_STORAGE_KEY, data.token);
};

const loginCognito = (pool: CognitoUserPool, creds: AuthenticationRequest): Promise<void> =>
  new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: creds.email,
      Password: creds.password,
    });
    userPool(pool, creds.email).authenticateUser(authenticationDetails, {
      onSuccess: async (result) => {
        await storage.set(TOKEN_STORAGE_KEY, result.getAccessToken()?.getJwtToken());
        resolve();
      },
      onFailure: (err) => reject(err),
    });
  });

const isCognitoUserConfirmed = (pool: CognitoUserPool, creds: SignupRequest): Promise<boolean> =>
  new Promise((resolve) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: creds.email,
      Password: creds.password,
    });
    userPool(pool, creds.email).authenticateUser(authenticationDetails, {
      onSuccess: () => resolve(true),
      onFailure: (err) => resolve(!isCognitoError(err, "UserNotConfirmedException")),
    });
  });

const resendCognitoConfirmation = (pool: CognitoUserPool, creds: SignupRequest): Promise<void> =>
  new Promise((resolve, reject) => {
    userPool(pool, creds.email).resendConfirmationCode(
      (err) => (err ? reject(err) : resolve()),
      signupClientMetadata(creds.alias, creds.name, creds.surname),
    );
  });

const signupStandard = async (creds: SignupRequest): Promise<void> => {
  await axios.post<UserResponse>(`${API_ENDPOINT}/users`, creds);
};

const signupCognito = async (pool: CognitoUserPool, creds: SignupRequest): Promise<string> =>
  new Promise((resolve, reject) => {
    const userAttributes = [new CognitoUserAttribute({ Name: "email", Value: creds.email })];
    const userID = uuid();

    pool.signUp(
      userID,
      creds.password,
      userAttributes,
      [], // validationData
      async function (err) {
        if (!err) {
          resolve(userID);
          return;
        }

        if (
          isCognitoError(err, "UsernameExistsException") &&
          !(await isCognitoUserConfirmed(pool, creds))
        ) {
          resendCognitoConfirmation(pool, creds)
            .then(() => resolve(userID))
            .catch(reject);
          return;
        }

        reject(err);
      },
      signupClientMetadata(creds.alias, creds.name, creds.surname),
    );
  });

const confirmSignupCognito = (pool: CognitoUserPool, req: ConfirmSignupRequest): Promise<void> =>
  new Promise((resolve, reject) => {
    userPool(pool, req.userID).confirmRegistration(
      req.code,
      false,
      (err) => (err ? reject(err) : resolve()),
      signupClientMetadata(req.alias, req.name, req.surname),
    );
  });

const resetPasswordCognito = (pool: CognitoUserPool, req: ResetPasswordRequest): Promise<void> =>
  new Promise((resolve, reject) => {
    userPool(pool, req.email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });

const confirmPasswordResetCognito = (
  pool: CognitoUserPool,
  req: ConfirmResetPasswordRequest,
): Promise<void> =>
  new Promise((resolve, reject) => {
    userPool(pool, req.email).confirmPassword(req.code, req.newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });

const getAuthFn = <
  SF extends (...args: any[]) => Promise<void>,
  CF extends (pool: CognitoUserPool, ...args: any[]) => Promise<void | string>,
>(
  standardFn: SF,
  cognitoFn: CF,
) =>
  COGNITO_USER_POOL_ID && COGNITO_CLIENT_ID
    ? cognitoFn.bind(
        this,
        new CognitoUserPool({
          UserPoolId: COGNITO_USER_POOL_ID,
          ClientId: COGNITO_CLIENT_ID,
        }),
      )
    : standardFn;

const noop = (): Promise<void> => Promise.resolve();

export const login = getAuthFn(loginStandard, loginCognito);
export const signup = getAuthFn(signupStandard, signupCognito) as (
  ...args: any[]
) => Promise<string>;
export const confirmSignup = getAuthFn(noop, confirmSignupCognito);
export const resetPassword = getAuthFn(noop, resetPasswordCognito);
export const confirmResetPassword = getAuthFn(noop, confirmPasswordResetCognito);
export const logout = () => storage.clear();

export const loginWithGoogle = (): void => {
  const params = new URLSearchParams({
    identity_provider: "Google",
    response_type: "code",
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: COGNITO_REDIRECT_URI,
    scope: "email openid profile",
  });
  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
};

export const handleOAuthCallback = async (code: string): Promise<void> => {
  const params = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: COGNITO_CLIENT_ID,
    code,
    redirect_uri: COGNITO_REDIRECT_URI,
  });
  const { data } = await axios.post(`https://${COGNITO_DOMAIN}/oauth2/token`, params.toString(), {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  await storage.set(TOKEN_STORAGE_KEY, data.access_token);
};
